import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import SignatureCanvas from 'react-signature-canvas';
import ModalHeader from './modal/ModalHeader';
import ModalFooter from './modal/ModalFooter';
import Modal from './modal/Modal';
import i18n from '../services/i18n';

const SignatureModal = forwardRef((props, ref) => {
  const {
    id,
    wrapperClass,
    initialSignature,
    penColor,
    backgroundColor,
    showClear,
    onClose,
    title,
    showConfirm,
    disabled,
  } = props;
  const signatureRef = useRef(null); // Ref for the SignatureCanvas
  const [touched, setTouched] = useState(!!initialSignature || false);

  useEffect(() => {
    // Set the initial signature if it exists
    if (initialSignature && signatureRef.current) {
      signatureRef.current.fromDataURL(initialSignature);
    }
  }, [initialSignature]);

  const clearSignature = () => {
    setTouched(false);
    signatureRef.current.clear();
  };

  const handleMouseDown = () => {
    if (!touched) {
      setTouched(true);
    }
  };

  const saveSignature = () => signatureRef.current?.toDataURL();

  const checkIfEmpty = () =>
    signatureRef.current && signatureRef.current.isEmpty() && !touched;

  // Expose methods through the forwarded ref
  useImperativeHandle(ref, () => ({
    clearSignature,
    saveSignature,
    checkIfEmpty,
    fromDataURL: signatureRef.current ? signatureRef.current.fromDataURL : null,
    toDataURL: signatureRef.current ? signatureRef.current.toDataURL : null,
  }));

  const handleSavePopup = () => onClose(saveSignature());
  const handleClosePopup = () => onClose(null);

  useEffect(() => {
    if (signatureRef.current) {
      if (disabled) {
        signatureRef.current.off();
      }
    }
  }, [disabled]);

  return (
    <Modal onClose={handleClosePopup} id={id}>
      <div
        className={`modal-dialog modal-dialog-centered modal-lg ${wrapperClass}`}
        role="document"
      >
        <div className="modal-content">
          {title ? <ModalHeader title={title} /> : null}
          <div className="signature-container">
            <div className="container">
              <SignatureCanvas
                ref={signatureRef}
                penColor={penColor}
                backgroundColor={backgroundColor}
                canvasProps={{
                  width: 800,
                  height: 200,
                  onMouseDown: handleMouseDown,
                  pointerEvents: 'none',
                }}
              />
              {showClear && touched ? (
                <button
                  className="btn-clear-signature"
                  type="button"
                  onClick={clearSignature}
                >
                  {i18n.__('clear')}
                </button>
              ) : null}
            </div>
          </div>
          <ModalFooter
            onSave={handleSavePopup}
            cancelText={i18n.__('cancel')}
            onCancel={handleClosePopup}
            saveText={showConfirm ? i18n.__('confirm') : null}
          />
        </div>
      </div>
    </Modal>
  );
});

SignatureModal.propTypes = {
  penColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  initialSignature: PropTypes.string,
  showClear: PropTypes.bool,
  onClose: PropTypes.func,
  title: PropTypes.string,
  id: PropTypes.string,
  wrapperClass: PropTypes.string,
  showConfirm: PropTypes.bool,
  disabled: PropTypes.bool,
};

SignatureModal.defaultProps = {
  backgroundColor: '#FAFAFD',
  penColor: '#000000',
  initialSignature: '',
  showClear: false,
  onClose: null,
  title: null,
  id: 'signatureModal',
  wrapperClass: '',
  showConfirm: true,
  disabled: false,
};

export default SignatureModal;
